import React, { useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import './CodeEditor.css';

const CodeEditor = ({ value, onChange, language = 'html', height = '400px', readOnly = false }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    // Configure Monaco Editor for autocomplete features
    if (window.monaco) {
      // Auto-close HTML tags
      window.monaco.languages.html.htmlDefaults.setOptions({
        format: {
          enabled: true
        }
      });

      // Configure JavaScript/TypeScript for document autocomplete
      window.monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: window.monaco.languages.typescript.ScriptTarget.ES2020,
        allowNonTsExtensions: true,
        lib: ['ES2020', 'DOM']
      });
      

      // Add custom autocomplete for 'doc' -> 'document'
     /* window.monaco.languages.registerCompletionItemProvider('javascript', {
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn
          };

          // If user types 'doc', suggest 'document'
          if (word.word.toLowerCase().startsWith('doc')) {
            return {
              suggestions: [
                {
                  label: 'document',
                  kind: window.monaco.languages.CompletionItemKind.Variable,
                  insertText: 'document',
                  range: range,
                  detail: 'DOM Document Object'
                },
                {
                  label: 'document.getElementById',
                  kind: window.monaco.languages.CompletionItemKind.Method,
                  insertText: 'document.getElementById(${1:\'id\'})',
                  insertTextRules: window.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  range: range,
                  detail: 'Get element by ID'
                },
                {
                  label: 'document.querySelector',
                  kind: window.monaco.languages.CompletionItemKind.Method,
                  insertText: 'document.querySelector(${1:\'selector\'})',
                  insertTextRules: window.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  range: range,
                  detail: 'Query selector'
                },
                {
                  label: 'document.querySelectorAll',
                  kind: window.monaco.languages.CompletionItemKind.Method,
                  insertText: 'document.querySelectorAll(${1:\'selector\'})',
                  insertTextRules: window.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  range: range,
                  detail: 'Query selector all'
                },
                {
                  label: 'document.createElement',
                  kind: window.monaco.languages.CompletionItemKind.Method,
                  insertText: 'document.createElement(${1:\'tagName\'})',
                  insertTextRules: window.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  range: range,
                  detail: 'Create element'
                }
              ]
            };
          }
          return { suggestions: [] };
        }
      }); */

      // Add custom autocomplete for HTML context
    /*   window.monaco.languages.registerCompletionItemProvider('html', {
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn
          };

          // If user types 'doc' in HTML context, suggest 'document'
          if (word.word.toLowerCase().startsWith('doc')) {
            return {
              suggestions: [
                {
                  label: 'document',
                  kind: window.monaco.languages.CompletionItemKind.Variable,
                  insertText: 'document',
                  range: range,
                  detail: 'DOM Document Object'
                }
              ]
            };
          }
          return { suggestions: [] };
        }
      });*/
    }
  }, []);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Enable auto-closing brackets and tags
    editor.updateOptions({
      autoClosingBrackets: 'always',
      autoClosingQuotes: 'always',
      autoIndent: 'full',
      formatOnPaste: true,
      formatOnType: true
    });

    // Custom auto-closing for HTML tags
    editor.onKeyDown((e) => {
      // Auto-close HTML tags when typing '>'
      if (e.keyCode === monaco.KeyCode.Enter && language === 'html') {
        const model = editor.getModel();
        const position = editor.getPosition();
        const line = model.getLineContent(position.lineNumber - 1);
        
        // Check if we're inside an opening tag
        const tagMatch = line.match(/<(\w+)[^>]*>$/);
        if (tagMatch && !line.includes('</')) {
          const tagName = tagMatch[1];
          // Don't auto-close self-closing tags
          const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
          if (!selfClosingTags.includes(tagName.toLowerCase())) {
            const indent = line.match(/^(\s*)/)[1];
            const insertText = `\n${indent}  \n${indent}</${tagName}>`;
            editor.executeEdits('auto-close-tag', [{
              range: new monaco.Range(
                position.lineNumber,
                position.column,
                position.lineNumber,
                position.column
              ),
              text: insertText
            }]);
            // Move cursor to the middle line
            editor.setPosition({
              lineNumber: position.lineNumber + 1,
              column: indent.length + 3
            });
            e.preventDefault();
          }
        }
      }
    });
  };

  const getLanguage = () => {
    switch (language) {
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      case 'javascript':
        return 'javascript';
      case 'mixed':
        return 'html'; // Default to HTML for mixed
      default:
        return 'html';
    }
  };

  return (
    <div className="code-editor-container">
      <Editor
        height={height}
        language={getLanguage()}
        value={value}
        onChange={onChange}
        onMount={handleEditorDidMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          readOnly: readOnly,
          ...(readOnly ? {} : {
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            formatOnPaste: true,
            formatOnType: true
          })
        }}
      />
    </div>
  );
};

export default CodeEditor;
